import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import SchoolIcon from "@mui/icons-material/School";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useNavigate } from "react-router-dom";
import { useFlowState, useFlowActions } from "../../state/FlowProvider";
import {
  GOAL_IDS,
  MIN_GOALS,
  nextStep,
  ONBOARDING_BASE_PATH,
} from "../registry";
import GoalCard from "../../components/Goals/GoalCard";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";
import FormSubmitButton from "../../components/FormSubmitButton";

/**
 * Goal selection — the step that replaces the old mentor/mentee role choice.
 *
 * Goals, not roles, are what the redesign collects: a member who wants to
 * mentor says so by choosing that goal, and the capability follows from what
 * they do rather than from a box ticked at signup (ECH-107).
 *
 * The selection also decides which sub-flows run next, so this screen is the
 * input to the whole of `person_flow`. It deliberately does not know that —
 * the registry derives reachability from `goals`, and ECH-116's sub-flow host
 * derives its sequence from the same array.
 *
 * Icons are attached here rather than beside `GOAL_IDS` in the registry, which
 * is deliberately free of JSX so it stays a plain, testable model of the flow —
 * the same split as `routes.jsx`, where components attach to registry steps.
 */
/** Elements, not components: `GoalCard` renders `icon` directly as a child. */
export const GOAL_ICONS = {
  grow_career: <TrendingUpIcon />,
  networking: <GroupsIcon />,
  provide_mentorship: <SupervisorAccountIcon />,
  receive_mentorship: <SchoolIcon />,
  build_skills: <AutoAwesomeIcon />,
};

const continueLabel = (count) => {
  if (count === 0) return t("onboarding.goals.continueNone");
  if (count === 1) return t("onboarding.goals.continueOne");
  return t("onboarding.goals.continueMany", { count });
};

const GoalsStep = ({ step }) => {
  const state = useFlowState();
  const { setGoals } = useFlowActions();
  const navigate = useNavigate();

  // Seeded from the flow so returning to this step shows what was chosen.
  const [selected, setSelected] = useState(() => state.goals ?? []);

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id],
    );

  const canContinue = selected.length >= MIN_GOALS;

  const handleContinue = () => {
    if (!canContinue) return;

    setGoals(selected);

    // Routed against the state this answer produces: dispatch has not landed
    // yet, so the registry would otherwise be asked about a flow with no goals
    // and refuse to advance.
    const next = nextStep(step.id, { ...state, goals: selected });
    if (next) navigate(`${ONBOARDING_BASE_PATH}/${next.path}`);
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleContinue();
      }}
    >
      <Typography variant="h1" sx={{ mb: 1.5 }}>
        {t("onboarding.goals.heading")}
      </Typography>

      <Typography
        sx={{
          color: onboardingTokens.mutedForeground,
          lineHeight: 1.65,
          mb: 4,
        }}
      >
        {t("onboarding.goals.body")}
      </Typography>

      {/* A group rather than a list of standalone buttons, so a screen reader
          announces how many options there are and that they belong together. */}
      <Stack
        gap={1.25}
        role="group"
        aria-label={t("onboarding.goals.legend")}
        sx={{ mb: 4 }}
      >
        {GOAL_IDS.map((id) => (
          <GoalCard
            key={id}
            label={t(`onboarding.goals.options.${id}.label`)}
            description={t(`onboarding.goals.options.${id}.description`)}
            icon={GOAL_ICONS[id]}
            selected={selected.includes(id)}
            onToggle={() => toggle(id)}
          />
        ))}
      </Stack>

      <FormSubmitButton
        variant="contained"
        fullWidth
        disabledReason={!canContinue ? continueLabel(0) : ""}
        sx={{ py: 1.5 }}
      >
        {continueLabel(selected.length)}
      </FormSubmitButton>
    </Box>
  );
};

export default GoalsStep;
